/**
 * EidAdhaDecoration — modern festive header strip for Eid Al Adha.
 *
 * Visually paired with `EidAdhaHomeBanner`: same soft pastel palette,
 * same gold-emerald accent rule, and a real chibi sheep as a small
 * round avatar next to the Arabic greeting. Replaces the prior deep-
 * emerald bar with something airy and modern.
 *
 * Mobile = single-line: sheep avatar + Arabic greeting + sparkle.
 * Desktop = adds an English sub-label and accent rules either side.
 *
 * Decorative motion is gated behind `motion-safe:` so users with
 * `prefers-reduced-motion` see a static composition.
 */

import sheep1 from "@assets/images/sheep-1.png";
import { cn } from "@design-system/lib/utils";

// ── Inline ornaments ─────────────────────────────────────────────────────

function Sparkle({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={cn("flex-shrink-0", className)} aria-hidden="true">
      <path
        d="M12 2 L13.5 10.5 L22 12 L13.5 13.5 L12 22 L10.5 13.5 L2 12 L10.5 10.5 Z"
        fill="currentColor"
      />
    </svg>
  );
}

function Crescent({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={cn("flex-shrink-0", className)} aria-hidden="true">
      <defs>
        <mask id="adha-strip-crescent">
          <rect width="24" height="24" fill="white" />
          <circle cx="16" cy="12" r="9" fill="black" />
        </mask>
      </defs>
      <circle cx="12" cy="12" r="9" fill="currentColor" mask="url(#adha-strip-crescent)" />
    </svg>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

export function EidAdhaDecoration() {
  return (
    <div className="relative w-full overflow-hidden" dir="rtl">
      {/* Thin top rule — gold gradient hairline */}
      <div className="h-px w-full bg-gradient-to-r from-transparent via-amber-400 to-transparent" />

      {/* Main strip — airy pastel gradient (mirrors the home banner) */}
      <div
        className={cn(
          "relative flex items-center justify-center gap-2 px-3 py-1.5 sm:gap-4 sm:px-6 sm:py-2",
          "bg-gradient-to-r from-amber-50 via-rose-50 to-emerald-50",
        )}
      >
        {/* Soft amber blob top-left for atmosphere */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -left-10 -top-6 h-16 w-16 rounded-full bg-amber-200/40 blur-2xl"
        />
        {/* Soft emerald blob bottom-right */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-6 -right-10 h-16 w-16 rounded-full bg-emerald-200/40 blur-2xl"
        />

        {/* Left ornament cluster — desktop only */}
        <div className="hidden items-center gap-2 sm:flex">
          <div className="h-px w-10 bg-gradient-to-r from-transparent to-amber-400/60" />
          <Crescent className="h-3.5 w-3.5 text-emerald-600 motion-safe:animate-[sway_4s_ease-in-out_infinite]" />
          <Sparkle className="h-3 w-3 text-amber-500" />
        </div>

        {/* Sheep avatar — sits inline with the text on every viewport */}
        <span
          className={cn(
            "relative inline-flex h-6 w-6 flex-shrink-0 items-center justify-center",
            "rounded-full bg-white ring-2 ring-amber-300 shadow-sm",
            "sm:h-7 sm:w-7",
          )}
          aria-hidden="true"
        >
          <img
            src={sheep1}
            alt=""
            loading="eager"
            decoding="async"
            className={cn(
              "h-5 w-5 object-contain sm:h-6 sm:w-6",
              "motion-safe:animate-[float-up_3.5s_ease-in-out_infinite]",
            )}
          />
        </span>

        {/* Centre — Arabic greeting + (desktop only) tiny English sub-label */}
        <span
          className={cn(
            "flex items-center gap-2 text-emerald-900",
            "text-xs font-bold tracking-tight sm:text-sm sm:tracking-wide",
          )}
          style={{
            fontFamily: '"Noto Naskh Arabic", "Amiri", "Segoe UI", Tahoma, serif',
          }}
        >
          عيد الأضحى المبارك
          {/* Desktop-only English caption */}
          <span
            className="hidden text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-700/70 sm:inline"
            style={{ fontFamily: '"Inter", "Segoe UI", sans-serif' }}
          >
            · 1447
          </span>
        </span>

        {/* Right ornament cluster — desktop only, mirrored */}
        <div className="hidden items-center gap-2 sm:flex">
          <Sparkle className="h-3 w-3 text-amber-500" />
          <Crescent className="h-3.5 w-3.5 -scale-x-100 text-emerald-600 motion-safe:animate-[sway_4s_ease-in-out_infinite_0.5s]" />
          <div className="h-px w-10 bg-gradient-to-l from-transparent to-amber-400/60" />
        </div>

        {/* Mobile-only trailing sparkle */}
        <Sparkle className="h-3 w-3 flex-shrink-0 text-amber-500 sm:hidden" />
      </div>

      {/* Thin bottom rule — emerald gradient hairline */}
      <div className="h-px w-full bg-gradient-to-r from-transparent via-emerald-400/60 to-transparent" />
    </div>
  );
}
